import React, { useEffect, useState, useCallback } from 'react';
import { Activity, AlertTriangle, RefreshCw, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Live picture of failed offline-queue submissions across all users. Powered
 * by /api/admin/sw/queue-health which the SW reports failures to via
 * /api/sw/queue-failure (PII-free — only path + status + short error).
 */
const SwQueueHealthWidget = ({ token }) => {
  const [data, setData] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [emailingId, setEmailingId] = useState(null);
  const [emailMessage, setEmailMessage] = useState('');

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/sw/queue-health`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/pulse/grouped?type=sw_submission_failed&status=open&limit=20`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(r1.data);
      setPulse(r2.data);
    } catch (e) {
      // Silent — don't break the dashboard
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [load]);

  const wipe = async () => {
    if (!window.confirm('Clear ALL recorded SW failures? This is permanent.')) return;
    setBusy(true);
    try {
      const r = await axios.delete(`${API}/admin/sw/queue-health/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Cleared ${r.data.deleted} record(s)`);
      load();
    } catch (e) {
      toast.error('Could not clear');
    } finally {
      setBusy(false);
    }
  };

  const sendEmail = async (group) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/pulse/${group.latestPulseId}/email-user`,
        { message: emailMessage },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Emailed ${group.userEmail}`);
      setEmailingId(null);
      setEmailMessage('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send email');
    } finally {
      setBusy(false);
    }
  };

  const resolveItem = async (group) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/pulse/user/${group.userId}/resolve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Resolved ${r.data.resolved} item${r.data.resolved === 1 ? '' : 's'}`);
      load();
    } catch (e) {
      toast.error('Could not resolve');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  const { total24h, total7d, byPath24h, byStatus24h, byKind24h, recent } = data;
  // Severity colour: 0 → green calm, 1-9 → amber, 10+ → red
  const severity = total24h >= 10 ? 'red' : total24h > 0 ? 'amber' : 'green';
  const tone = {
    green: { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-700', heading: 'text-emerald-900' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-300', icon: 'text-amber-700', heading: 'text-amber-900' },
    red: { bg: 'bg-red-50', border: 'border-red-300', icon: 'text-red-700', heading: 'text-red-900' },
  }[severity];

  return (
    <div className={`${tone.bg} ${tone.border} border-2 rounded-xl p-4 mb-4`} data-testid="sw-queue-health-widget">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          {severity === 'green'
            ? <Activity className={`h-5 w-5 mt-0.5 ${tone.icon}`} />
            : <AlertTriangle className={`h-5 w-5 mt-0.5 ${tone.icon}`} />}
          <div className="flex-1">
            <h3 className={`text-base font-semibold ${tone.heading}`}>
              {severity === 'green'
                ? 'Submission queue healthy'
                : severity === 'amber'
                  ? `${total24h} failed submission${total24h !== 1 ? 's' : ''} in last 24h`
                  : `${total24h} failed submissions in last 24h — investigate`}
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              {total7d} total in last 7 days · auto-refreshes every minute
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="p-1.5 rounded hover:bg-white/60 text-gray-600"
            title="Refresh"
            data-testid="sw-health-refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {total7d > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="p-1.5 rounded hover:bg-white/60 text-gray-600"
              title={expanded ? 'Collapse' : 'Expand'}
              data-testid="sw-health-toggle"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Active user-attributable issues — one card per logged-in user with
          their failure count. Anonymous reports do NOT show up here. */}
      {pulse?.openUserCount > 0 && (
        <div className="mt-4 bg-white rounded-lg border border-red-200 p-3" data-testid="sw-health-pulse-section">
          <h4 className="text-xs font-semibold text-red-800 uppercase tracking-wide mb-2 flex items-center gap-2">
            Active users with failed submissions · {pulse.openUserCount}
          </h4>
          <ul className="space-y-2">
            {pulse.rows.slice(0, 5).map((group) => (
              <li
                key={group.userId}
                className="flex items-start justify-between gap-3 bg-red-50 rounded p-2 border border-red-100"
                data-testid={`pulse-user-${group.userId}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {group.businessName || group.userFullName || group.userEmail}
                    </p>
                    <span
                      className="text-[11px] font-bold bg-red-600 text-white px-1.5 py-0.5 rounded-full"
                      data-testid={`pulse-user-count-${group.userId}`}
                      title={`${group.failureCount} failed submission${group.failureCount === 1 ? '' : 's'} across ${group.uniquePathCount} endpoint${group.uniquePathCount === 1 ? '' : 's'}`}
                    >
                      {group.failureCount} fail{group.failureCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 truncate">
                    <span className="font-mono">{group.userEmail}</span>
                    {' · '}
                    Latest: <span className="font-mono">{group.lastPath}</span>
                    {' · '}
                    {formatTime(group.lastCreatedAt)}
                  </p>
                  {group.lastError && (
                    <p className="text-xs text-red-700 truncate mt-0.5" title={group.lastError}>
                      {group.lastError}
                    </p>
                  )}
                  {emailingId === group.userId && (
                    <div className="mt-2">
                      <textarea
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value.slice(0, 1500))}
                        placeholder="Optional personal message (will appear in the email)"
                        rows={2}
                        className="w-full text-xs px-2 py-1 border border-gray-300 rounded resize-none"
                        data-testid={`pulse-email-message-${group.userId}`}
                      />
                      <div className="mt-1 flex items-center gap-2">
                        <button
                          onClick={() => sendEmail(group)}
                          disabled={busy}
                          className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded font-semibold"
                          data-testid={`pulse-email-confirm-${group.userId}`}
                        >
                          Send email to {group.userEmail}
                        </button>
                        <button
                          onClick={() => { setEmailingId(null); setEmailMessage(''); }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                          data-testid={`pulse-email-cancel-${group.userId}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {emailingId !== group.userId && (
                  <div className="flex-shrink-0 flex items-center gap-1">
                    {group.emailedCount >= group.failureCount ? (
                      <span className="text-xs text-emerald-700 px-2 py-1 bg-emerald-50 rounded" data-testid={`pulse-email-sent-${group.userId}`}>
                        ✓ Emailed
                      </span>
                    ) : (
                      <button
                        onClick={() => { setEmailingId(group.userId); setEmailMessage(''); }}
                        disabled={busy}
                        className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded font-medium"
                        data-testid={`pulse-email-btn-${group.userId}`}
                      >
                        Email user
                      </button>
                    )}
                    <button
                      onClick={() => resolveItem(group)}
                      disabled={busy}
                      className="text-xs text-gray-600 hover:text-gray-900 px-2 py-1 underline"
                      data-testid={`pulse-resolve-btn-${group.userId}`}
                      title={`Resolve all ${group.failureCount} item(s) for this user`}
                    >
                      Resolve all
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {pulse.openUserCount > 5 && (
            <p className="text-xs text-gray-500 mt-2">
              + {pulse.openUserCount - 5} more user{pulse.openUserCount - 5 === 1 ? '' : 's'} with open failures · expand the breakdown above to see all activity
            </p>
          )}
        </div>
      )}

      {expanded && total7d > 0 && (
        <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm">
          <Section title="By endpoint (24h)" rows={byPath24h.map(r => ({ key: r.key || '(unknown)', value: r.count }))} testIdPrefix="sw-health-path" />
          <Section title="By status code (24h)" rows={byStatus24h.map(r => ({ key: r.key === null ? 'network' : `HTTP ${r.key}`, value: r.count }))} testIdPrefix="sw-health-status" />
          <Section title="By failure kind (24h)" rows={byKind24h.map(r => ({ key: kindLabel(r.key), value: r.count }))} testIdPrefix="sw-health-kind" />
        </div>
      )}

      {expanded && recent && recent.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Recent (last 20)</h4>
          <div className="bg-white rounded-lg border border-gray-200 max-h-72 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 text-gray-600 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-left px-3 py-2">Endpoint</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Error</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((f, i) => (
                  <tr key={f.id || i} className="border-t border-gray-100" data-testid={`sw-health-recent-${i}`}>
                    <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">{formatTime(f.createdAt)}</td>
                    <td className="px-3 py-1.5 font-mono text-gray-800">{f.path}</td>
                    <td className="px-3 py-1.5 text-gray-700">{f.statusCode === null || f.statusCode === undefined ? 'network' : f.statusCode}</td>
                    <td className="px-3 py-1.5 text-gray-700 truncate max-w-xs" title={f.lastError}>{f.lastError || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={wipe}
              disabled={busy}
              className="text-xs text-red-700 hover:text-red-900 underline flex items-center gap-1"
              data-testid="sw-health-clear-all"
            >
              <Trash2 className="h-3 w-3" /> Clear all records
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, rows, testIdPrefix }) => (
  <div>
    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">{title}</h4>
    {rows.length === 0 ? (
      <p className="text-xs text-gray-500">—</p>
    ) : (
      <ul className="space-y-1">
        {rows.map((r, i) => (
          <li
            key={i}
            className="flex justify-between text-xs bg-white rounded px-2 py-1 border border-gray-100"
            data-testid={`${testIdPrefix}-${i}`}
          >
            <span className="font-mono truncate mr-2 text-gray-800">{r.key}</span>
            <span className="text-gray-700 font-semibold flex-shrink-0">{r.value}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

function kindLabel(kind) {
  switch (kind) {
    case 'permanent_4xx': return 'Validation / auth error';
    case 'gave_up': return 'Gave up after retries';
    case 'network': return 'Network unreachable';
    default: return kind || '(unknown)';
  }
}

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso.slice(0, 16).replace('T', ' ');
  }
}

export default SwQueueHealthWidget;
